import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LogOut, Download, ExternalLink } from "lucide-react";
import useSeo from "@/lib/useSeo";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminCohort from "@/pages/AdminCohort";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [emailLogs, setEmailLogs] = useState(null);

  useSeo({
    title: "Admin Dashboard — Langwood360",
    description: "Administrator dashboard.",
    path: "/admin",
    noindex: true,
  });

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/stats").then(({ data }) => setStats(data));
      api.get("/admin/submissions").then(({ data }) => setSubs(data));
      api.get("/admin/sessions").then(({ data }) => setSessions(data));
    }
  }, [user]);

  useEffect(() => {
    if (tab === "emails" && user && user.role === "admin") {
      api.get("/admin/email-logs").then(({ data }) => setEmailLogs(data));
    }
  }, [tab, user]);

  if (loading) return <div className="min-h-screen bg-linen"><Navbar /><div className="p-12 tick-label">Loading…</div></div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 pb-4">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="tick-label-accent mb-2">Admin Dashboard</div>
            <h1 className="display-serif text-4xl md:text-5xl">Signed in as {user.name || user.email}</h1>
          </div>
          <div className="flex gap-3">
            <a data-testid="admin-export-csv" href={`${API}/admin/export/submissions.csv`} target="_blank" rel="noreferrer" className="btn-ghost">
              <Download className="w-3.5 h-3.5" strokeWidth={1.5}/> Submissions CSV
            </a>
            <a data-testid="admin-export-questions-csv" href={`${API}/admin/export/questions.csv`} target="_blank" rel="noreferrer" className="btn-ghost">
              <Download className="w-3.5 h-3.5" strokeWidth={1.5}/> Questions CSV
            </a>
            <button data-testid="admin-logout" onClick={logout} className="btn-ghost"><LogOut className="w-3.5 h-3.5" strokeWidth={1.5}/> Log out</button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
        <div className="flex gap-6 border-b border-oxford/10">
          {["overview", "analytics", "cohort", "submissions", "sessions", "emails"].map(t => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`pb-3 tick-label ${tab === t ? "text-oxford border-b-2 border-orange" : "text-charcoal hover:text-oxford"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-oxford/10">
            <Stat label="Total Submissions" value={stats.total_submissions} />
            <Stat label="Board Sessions" value={stats.total_sessions} />
            <Stat label="Leads Captured" value={stats.leads_captured} />
            <Stat label="Average VRI" value={stats.average_vri} />
          </div>
        )}
        {tab === "overview" && stats && (
          <div className="mt-8 border border-oxford/15 bg-linen p-8">
            <div className="tick-label-accent mb-4">Band distribution</div>
            <div className="space-y-3">
              {Object.entries(stats.band_distribution).map(([band, count]) => (
                <div key={band} className="flex items-baseline justify-between border-b border-oxford/10 pb-2">
                  <div className="text-oxford">{band}</div>
                  <div className="font-mono text-oxford">{count}</div>
                </div>
              ))}
              {Object.keys(stats.band_distribution).length === 0 && <div className="text-charcoal text-sm">No submissions yet.</div>}
            </div>
          </div>
        )}

        {tab === "analytics" && (
          <div className="mt-8">
            <AdminAnalytics />
          </div>
        )}

        {tab === "cohort" && (
          <div className="mt-8" data-testid="admin-cohort-panel">
            <AdminCohort />
          </div>
        )}

        {tab === "submissions" && (
          <div className="mt-8 border border-oxford/15 bg-linen overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-submissions-table">
              <thead>
                <tr className="border-b border-oxford/15 text-left">
                  <th className="tick-label p-4">When</th>
                  <th className="tick-label p-4">VRI</th>
                  <th className="tick-label p-4">Band</th>
                  <th className="tick-label p-4">Name</th>
                  <th className="tick-label p-4">Email</th>
                  <th className="tick-label p-4">Org</th>
                  <th className="tick-label p-4">Session</th>
                  <th className="tick-label p-4"></th>
                </tr>
              </thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.id} className="border-b border-oxford/10">
                    <td className="p-4 tick-label">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-4 font-mono text-oxford">{s.vri}</td>
                    <td className="p-4">{s.band}</td>
                    <td className="p-4">{s.contact?.name || s.participant_name || "—"}</td>
                    <td className="p-4">{s.contact?.email || "—"}</td>
                    <td className="p-4">{s.contact?.organisation || "—"}</td>
                    <td className="p-4 font-mono text-xs">{s.board_session_code || "—"}</td>
                    <td className="p-4"><Link to={`/results/${s.id}`} className="text-orange" target="_blank"><ExternalLink className="w-4 h-4"/></Link></td>
                  </tr>
                ))}
                {subs.length === 0 && <tr><td colSpan={8} className="p-8 text-center tick-label">No submissions yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "sessions" && (
          <div className="mt-8 border border-oxford/15 bg-linen overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-sessions-table">
              <thead>
                <tr className="border-b border-oxford/15 text-left">
                  <th className="tick-label p-4">Code</th>
                  <th className="tick-label p-4">Organisation</th>
                  <th className="tick-label p-4">Facilitator</th>
                  <th className="tick-label p-4">Email</th>
                  <th className="tick-label p-4">Responses</th>
                  <th className="tick-label p-4"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id} className="border-b border-oxford/10">
                    <td className="p-4 font-mono text-oxford">{s.code}</td>
                    <td className="p-4">{s.organisation}</td>
                    <td className="p-4">{s.facilitator_name}</td>
                    <td className="p-4">{s.facilitator_email}</td>
                    <td className="p-4 font-mono">{s.submission_count}{s.expected_participants ? ` / ${s.expected_participants}` : ""}</td>
                    <td className="p-4"><Link to={`/board/${s.code}/results?token=${s.facilitator_token}`} className="text-orange" target="_blank"><ExternalLink className="w-4 h-4"/></Link></td>
                  </tr>
                ))}
                {sessions.length === 0 && <tr><td colSpan={6} className="p-8 text-center tick-label">No sessions yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "emails" && (
          <div className="mt-8" data-testid="admin-emails-panel">
            <p className="text-sm text-charcoal mb-6 max-w-2xl">
              Email delivery health. A <span className="text-orange">sandbox fallback</span> means the branded sender was rejected (usually domain verification) and the email went out via the Resend sandbox instead. A <span className="text-heritage">failed</span> entry means the email never left — check your Resend DNS records.
            </p>
            {emailLogs && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-oxford/10 mb-8">
                <Stat label="Failed sends (all time)" value={emailLogs.failed_total} />
                <Stat label="Sandbox fallbacks (all time)" value={emailLogs.fallback_total} />
              </div>
            )}
            <div className="border border-oxford/15 bg-linen overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-email-logs-table">
                <thead>
                  <tr className="border-b border-oxford/15 text-left">
                    <th className="tick-label p-4">When</th>
                    <th className="tick-label p-4">Email</th>
                    <th className="tick-label p-4">Recipient</th>
                    <th className="tick-label p-4">Status</th>
                    <th className="tick-label p-4">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {(emailLogs?.logs || []).map((l, i) => (
                    <tr key={i} className="border-b border-oxford/10" data-testid={`email-log-row-${i}`}>
                      <td className="p-4 tick-label">{(l.at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-4">{l.label}</td>
                      <td className="p-4 font-mono text-xs">{(l.to || []).join(", ") || "—"}</td>
                      <td className="p-4">
                        <span data-testid={`email-log-status-${i}`} className={`tick-label ${l.status === "failed" ? "text-heritage" : "text-orange"}`}>
                          {l.status === "failed" ? "Failed" : "Sandbox fallback"}
                        </span>
                      </td>
                      <td className="p-4 text-charcoal text-xs max-w-md truncate" title={l.error}>{l.error}</td>
                    </tr>
                  ))}
                  {emailLogs && emailLogs.logs.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center tick-label" data-testid="email-logs-empty">No delivery problems recorded — all clear.</td></tr>
                  )}
                  {!emailLogs && <tr><td colSpan={5} className="p-8 text-center tick-label">Loading…</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-linen p-8">
      <div className="tick-label">{label}</div>
      <div className="mt-3 font-mono text-4xl text-oxford">{value ?? "—"}</div>
    </div>
  );
}
